import { useEffect, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import { useContentstackPage } from '../../../hooks/useContentstack';
import type { PageComponentItem } from '../../../types/contentstack';
import ChevronRightIcon from '../../../components/icons/ChevronRightIcon';
import { CmsHpsfHeroBanner } from '../../../components/CmsHpsfHeroBanner/CmsHpsfHeroBanner';

interface VideoItem {
  headline: string;
  body: string;
  image: { url: string; description?: string } | null;
  video?: { url: string } | null;
  buttons?: [];
}

const SLUG = 'portal_tutorials';
const CMS_COMPONENT_NAME = 'CmsHeroBannerCarousel';

const findCarouselData = (components: PageComponentItem[]) => {
  const component = components.find((c) => {
    const key = Object.keys(c)[0];
    return (c as PageComponentItem)[key]?.component === CMS_COMPONENT_NAME;
  });
  if (!component) return null;
  const key = Object.keys(component)[0];
  return (component as PageComponentItem)[key];
};

const PortalTutorials = () => {
  const { t } = useTranslation();
  const { data: pageData, loading, error } = useContentstackPage(SLUG);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  useEffect(() => {
    document.title = t(TranslationKeyEnum.Tutorials);
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t(TranslationKeyEnum.LoadingPage)}</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-gray-600">{t(TranslationKeyEnum.FailedToLoadTutorials)}</p>
      </div>
    );
  }

  const carouselData = findCarouselData(pageData.components as PageComponentItem[]);
  if (!carouselData) return null;

  const { title } = pageData;
  const description = carouselData.name as string;
  const items = carouselData.items as VideoItem[];

  return (
    <>
      <div className="flex flex-col justify-center items-start gap-4 w-[628px]">
        {title && (
          <h2 className="text-detail-150 font-bold text-[#192026]">{title}</h2>
        )}
        {description && (
          <p className="text-detail-100 text-[#192026]">{description}</p>
        )}
        <div className="flex flex-col rounded-[20px] border border-[#D1D5DB] p-2 w-full">
          {items.map((item) => (
            <button
              key={item.headline}
              onClick={() => setSelectedVideo(item)}
              className="flex justify-between items-center px-[14px] py-[12px] w-full text-left rounded-[10px] hover:bg-gray-50 transition-colors"
            >
              <span className="text-detail-100 text-[#192026]">{item.headline}</span>
              <ChevronRightIcon width={20} height={20} fill="#0054A4" />
            </button>
          ))}
        </div>
      </div>

      {selectedVideo && (
        <CmsHpsfHeroBanner
          videoOnly
          data={{
            items: [{ ...selectedVideo, buttons: selectedVideo.buttons ?? [] }],
          }}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </>
  );
};

export default PortalTutorials;
